import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'

// Z73.18 appended spec: goods_locations admin page -- AA.33 GDS_LOCN codes. Gary's 3 confirmed
// CY UK ETSF codes are seeded (migration 20260913e); this page lets him add more (partner
// hauliers' ETSFs, bonded warehouses at ports, etc.) as they're confirmed, same "short-term
// manual entry, long-term maybe automated" note as the migration's own comment.

interface GoodsLocation {
  code: string
  operator_name: string
  type: string
  address: string | null
  city: string | null
  postcode: string | null
  area: string | null
  is_own: boolean
  is_active: boolean
}

const empty: GoodsLocation = { code: '', operator_name: '', type: 'ETSF', address: '', city: '', postcode: '', area: '', is_own: false, is_active: true }
const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

export default function GoodsLocations() {
  const [rows, setRows] = useState<GoodsLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<GoodsLocation>(empty)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('goods_locations').select('*').order('code')
    setRows((data as GoodsLocation[]) || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openAdd = () => { setForm(empty); setError(null); setModal(true) }
  const openEdit = (r: GoodsLocation) => { setForm({ ...r }); setError(null); setModal(true) }
  const set = (k: keyof GoodsLocation, v: unknown) => setForm(p => ({ ...p, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.code.trim() || !form.operator_name.trim()) { setError('Code and Operator Name are required.'); return }
    setSaving(true); setError(null)
    try {
      const { error: err } = await supabase.from('goods_locations').upsert(form)
      if (err) throw err
      setModal(false)
      load()
    } catch (err: any) {
      setError(err?.message || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (r: GoodsLocation) => {
    await supabase.from('goods_locations').update({ is_active: !r.is_active }).eq('code', r.code)
    load()
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/admin" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">&larr; Admin</Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">📋 Customs — Goods Locations</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            AA.33 Goods Location codes (ETSFs, bonded warehouses etc.) for eCustoms declarations. "Own" locations are CY UK's own premises and get suggested by area.
          </p>
        </div>

        <div className="flex justify-end mb-4">
          <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            + Add Location
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-gray-400">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  {['Code', 'Operator', 'Type', 'Address', 'Area', 'Own', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(r => (
                  <tr key={r.code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white">{r.code}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{r.operator_name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.type}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{[r.address, r.city, r.postcode].filter(Boolean).join(', ')}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.area || '—'}</td>
                    <td className="px-4 py-3">{r.is_own && <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">CY UK</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-3">
                      <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                      <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                        {r.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No goods locations found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">{rows.length} location{rows.length !== 1 ? 's' : ''}</div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{rows.some(r => r.code === form.code) ? 'Edit Location' : 'Add Location'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code (GBAU...) *</label>
                  <input className={inputCls} value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} maxLength={42} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <input className={inputCls} value={form.type} onChange={e => set('type', e.target.value)} placeholder="ETSF / Bonded Warehouse / Port" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Operator Name *</label>
                <input className={inputCls} value={form.operator_name} onChange={e => set('operator_name', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                <input className={inputCls} value={form.address || ''} onChange={e => set('address', e.target.value)} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                  <input className={inputCls} value={form.city || ''} onChange={e => set('city', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Postcode</label>
                  <input className={inputCls} value={form.postcode || ''} onChange={e => set('postcode', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Area</label>
                  <input className={inputCls} value={form.area || ''} onChange={e => set('area', e.target.value.toUpperCase())} placeholder="BHX / LHR" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_own} onChange={e => set('is_own', e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">This is a CY UK-owned premises</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
